import { useTranslation } from 'react-i18next';
import { CircleNotchIcon } from '@phosphor-icons/react';
import type { DocumentNodeWithDetailsDto } from '@monorepo/shared';
import { DocumentStatus } from '@monorepo/shared';
import type { DocumentRowActions, DocumentRowPermissions } from './documentTableTypes';

interface DocumentActionButtonProps {
  document: DocumentNodeWithDetailsDto;
  permissions: DocumentRowPermissions;
  actions: DocumentRowActions;
  isLoading?: boolean;
}

type ActionConfig = {
  label: string;
  variant: 'primary' | 'disabled';
  onClick: () => void;
};

const useDraftAction = (
  document: DocumentNodeWithDetailsDto,
  permissions: DocumentRowPermissions,
  actions: DocumentRowActions,
  t: (key: string) => string,
): ActionConfig => {
  if (permissions.canSendForValidation && actions.onSendForValidation) {
    return {
      label: t('documents.table.actions.send_for_validation'),
      variant: 'primary',
      onClick: () => actions.onSendForValidation!(document),
    };
  }
  return {
    label: t('documents.table.actions.open'),
    variant: 'primary',
    onClick: () => actions.onOpen(document.id),
  };
};

const useValidationAction = (
  document: DocumentNodeWithDetailsDto,
  permissions: DocumentRowPermissions,
  actions: DocumentRowActions,
  t: (key: string) => string,
): ActionConfig => {
  const {
    isCurrentUserAssignedValidator,
    hasCurrentUserValidated,
    validatorCount,
    validatedValidatorCount,
  } = document;

  if (
    permissions.canValidate &&
    isCurrentUserAssignedValidator &&
    !hasCurrentUserValidated &&
    actions.onValidate
  ) {
    return {
      label: t('documents.table.actions.validate'),
      variant: 'primary',
      onClick: () => actions.onValidate!(document),
    };
  }

  if (permissions.canValidate && hasCurrentUserValidated) {
    if (validatorCount !== undefined && validatedValidatorCount !== undefined) {
      return {
        label: t('documents.table.actions.awaiting_validators', {
          validated: validatedValidatorCount,
          total: validatorCount,
        }),
        variant: 'disabled',
        onClick: () => {},
      };
    }

    return {
      label: t('documents.table.actions.validated'),
      variant: 'disabled',
      onClick: () => {},
    };
  }

  return {
    label: t('documents.table.actions.open'),
    variant: 'primary',
    onClick: () => actions.onOpen(document.id),
  };
};

const useDiffusionAction = (
  document: DocumentNodeWithDetailsDto,
  permissions: DocumentRowPermissions,
  actions: DocumentRowActions,
  t: (key: string) => string,
  isResend: boolean,
): ActionConfig => {
  if (permissions.canDiffuse) {
    if (isResend && actions.onResend) {
      return {
        label: t('documents.table.actions.resend'),
        variant: 'primary',
        onClick: () => actions.onResend!(document),
      };
    }
    if (!isResend && actions.onDiffuse) {
      return {
        label: t('documents.table.actions.diffuse'),
        variant: 'primary',
        onClick: () => actions.onDiffuse!(document),
      };
    }
  }
  return {
    label: t('documents.table.actions.open'),
    variant: 'primary',
    onClick: () => actions.onOpen(document.id),
  };
};

const getActionConfig = (
  document: DocumentNodeWithDetailsDto,
  permissions: DocumentRowPermissions,
  actions: DocumentRowActions,
  t: (key: string) => string,
): ActionConfig => {
  switch (document.status) {
    case DocumentStatus.DRAFT:
      return useDraftAction(document, permissions, actions, t);

    case DocumentStatus.PENDING_VALIDATION:
      return useValidationAction(document, permissions, actions, t);

    case DocumentStatus.VALIDATED:
      return useDiffusionAction(document, permissions, actions, t, false);

    case DocumentStatus.DIFFUSED:
      return useDiffusionAction(document, permissions, actions, t, true);

    case DocumentStatus.ARCHIVED:
      return {
        label: t('documents.table.actions.completed'),
        variant: 'disabled',
        onClick: () => {},
      };

    default:
      return {
        label: t('documents.table.actions.open'),
        variant: 'primary',
        onClick: () => actions.onOpen(document.id),
      };
  }
};

export const DocumentActionButton = ({
  document,
  permissions,
  actions,
  isLoading,
}: DocumentActionButtonProps) => {
  const { t } = useTranslation();
  const config = getActionConfig(document, permissions, actions, t);
  const disabled = config.variant === 'disabled' || isLoading;

  return (
    <button
      onClick={config.onClick}
      disabled={disabled}
      className={`w-full lg:w-auto px-4 py-1.5 text-sm font-medium rounded-md transition flex items-center justify-center gap-2 ${
        disabled
          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
          : 'bg-primary text-white hover:bg-opacity-90'
      }`}
    >
      {isLoading && <CircleNotchIcon size={16} className="animate-spin" weight="bold" />}
      {config.label}
    </button>
  );
};
